<template>
  <div>
    <modal :name="name"
           :styles="styles"
           :width="width_in_pixels"
           :height="height_in_pixels"
           :draggable="draggable_el"
           :adaptive="adaptive"
           :resizable="resizable"
           :min-width="minWidth"
           :min-height="minHeight"
           :max-width="maxWidth"
           :max-height="maxHeight"
           :scrollable="scrollable"
           :shift-x="shiftX"
           :shift-y="shiftY"
           :focus-trap="focusTrap"
           :overlay-transition="overlayTransition"
           :click-to-close="background_closeable"
           @before-close="ev => $emit('before-close', ev)"
           @before-open="emitBeforeOpen"
           @opened="emitOpened"
           @closed="emitClosed">
      <div class="vjs-modal-header drag-control" :class="stickyHeader && 'sticky'" :style="{ 'justify-content': titleJustify }">
        <div class="header-content">
          <h4 class="header-main">
            <i v-if="showIcon" :class="[ icon ]" class="mr-2" /> {{ title }}
          </h4>
        </div>
        <div class="header-controls">
          <slot name="header-controls" />
          <button v-if="closeButton"
                  type="button"
                  class="btn btn-icon btn-ss"
                  @click="$modal.hide(name)">
            <i class="la la-times" />
          </button>
        </div>
      </div>
      <div class="vjs-modal-body">
        <slot v-if="$scopedSlots[`default`]" />
        <slot v-else name="body-content" />
      </div>
      <div v-if="$scopedSlots['body-controls']" class="vjs-modal-btns">
        <slot name="body-controls" />
      </div>
    </modal>
  </div>
</template>

<script>
// all props referenced in template contained here:
import VjsModalMixin from './VjsModalMixin';

const getNextId = (function () {
  let id = 0;
  return () => id++;
})();

/**
 * This is a global register of all mounted vjs modals. Allowing vjs modals to see eachother is important so they don't step
 * on eachother's toes with regards to things like whether or not the body element should be scrollable when a modal is open.
 */
window.vjs_modals = window.vjs_modals ?? [];

export default {
  name: `vjs-modal`,
  mixins: [VjsModalMixin],
  data: () => ({
    is_open: false,
    foo: false,
    modal_id: null,
    old_sticky_z_index: parseInt(document.querySelector('.sticky-slot-container')?.style?.zIndex ?? 20),
  }),
  computed: {
    draggable_el() {
      if (typeof this.draggable === `boolean` && this.draggable === true) {
        return `.drag-control`;
      } else {
        return this.draggable;
      }
    },
    registered() {
      return window.vjs_modals.find(modal_config => modal_config.id === this.modal_id);
    },
  },
  mounted() {
    // get a new ID for this cmp
    this.modal_id = getNextId();
    // register this modal on `window.vjs_modals`
    this.registerModal();
    // set root element ID
    this.$el.id = `vjs-modal-${this.modal_id}`;

    EventBus.$on(`modal_close_${this.name}`, () => {
      this.$modal.hide(this.name);
    });
  },
  methods: {
    registerModal() {
      const config = {
        id: this.modal_id,
        component: this,
      };

      config.isLockingBodyScroll = function () {
        const cmp = this.component;
        return cmp.$props.bodyScroll === false && cmp._data.is_open === true;
      };

      window.vjs_modals.push(config);
    },
    /**
     * Horrible hack to get around z-indexing issue with modals & sticky bars.
     *
     * @param { number | string } index
     */
    setStickyIndexes(index) {
      const style_index = typeof index === 'number' ? parseInt(index).toString() : index;
      const els = [...document.querySelectorAll('.sticky-slot-container')];
      this.old_sticky_z_index = parseInt(els[0]?.style.zIndex);

      els.forEach(el => el.style.zIndex = style_index);
    },
    setBodyScrollable(unset = false) {
      const body_el = document.getElementById('kt_wrapper');

      if (!unset && !this.bodyScroll) {
        body_el.style.overflow = "hidden";
        body_el.style.height = '100vh';

        return;
      }

      const locked_by_other_modal = window.vjs_modals.some(modal_config => modal_config.isLockingBodyScroll() === true);
      if (locked_by_other_modal) return; // don't do anything if another modal wants the body to remain unscrollable

      // body_el.style.overflow = "auto";
      // body_el.style.position = "static";
      body_el.style = {};
    },
    emitBeforeOpen(ev) {
      window.vjs_modals.find(modal_config => modal_config.id == this.modal_id);

      this.setBodyScrollable();

      this.setStickyIndexes(1);

      this.$emit(`before-open`, ev);
    },
    emitOpened(ev) {
      this.is_open = true;

      this.$emit('opened', ev);
    },
    emitClosed() {
      this.is_open = false;

      if (!this.bodyScroll) { // we previously locked the body scroll...
        this.setBodyScrollable(true); // try to unset, may not be possible if another modal has a lock
      }

      this.setStickyIndexes(this.old_sticky_z_index);

      EventBus.$emit(`modal-complete`, {
        name: this.name,
        type: this.$options.name,
        data: this.close_data, // see mixin, override this in derivative components to return custom data on close
        hook: true, // mark this event as fired by the @closed hook
      });
      this.$emit('closed');
    },
  },
};
</script>

<style>
.sticky-slot-container {
  color: red !important;
}
</style>

<style lang="scss" scoped>
@use "sass:map";
@use "~AssetsSass/_variables" as *;

:deep(.vm--container) {
  display: flex;
  flex-direction: column;
  align-items: center;

  .vm--modal {
    border: 1px solid map.get($ss-adds, 'border-grey');
    border-radius: 4px;
    position: relative;
    overflow-y: auto;
    max-height: 83vh !important;
    max-width: 95vw !important;
    z-index: 30;
    top: 100px !important;
    left: unset !important;

    .vjs-modal-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      background-color: white;
      padding: 1.2em;
      border-bottom: 1px solid map.get($ss-adds, 'border-grey');
      margin-bottom: 1.2em;

      &.sticky {
        position: sticky;
        top: 0;
        z-index: 20;
      }

      .header-content {
        * {
          display: inline-block;
        }

        .header-main {
          display: flex;
          flex-direction: row;
          align-items: center;
          margin: 0;
        }
      }

      .header-controls {
        .drag-control {
          &:hover {
            cursor: grab;
            background: rgba(map.get($ss, 'secondary'), 0.3);
          }
          &:active {
            cursor: grabbing;
            background: rgba(map.get($ss, 'secondary'), 0.6);
            color: white;
          }
        }
      }
    }

    .vjs-modal-body {
      padding: 1.2em;
      padding-top: 0;
      font-size: 1.1em;
    }

    .vjs-modal-btns {
      position: sticky;
      bottom: 0;
      z-index: 20;
      background-color: white;
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: center;
      padding: 13px 0 13px 0;
      margin-top: 0.5em;
      border-top: 1px solid map.get($ss-adds, 'border-grey');
      button {
        margin: 0 0.5em;
        min-width: 90px;
      }
    }
  }
}
</style>
